/**
 * Transform-handle geometry — pure math for the resize/rotate interactions.
 *
 * The selection frame carries 8 perimeter resize handles + 1 rotate handle
 * floating above top-center. Hit-testing happens in SCREEN space (constant
 * touch target regardless of zoom); scaling math runs per frame as
 * worklets; commit-time patch builders run on the JS thread.
 *
 * Group-scale semantics (v1, industry-standard AABB scaling): every dragged
 * object's frame scales relative to the ANCHOR (the point opposite the
 * dragged handle) — `x' = ax + (x − ax) · sx`, `w' = w · sx`. Rotated
 * objects keep their rotation and scale their frame — a deliberate
 * approximation (true local-axis resize of a rotated object under
 * non-uniform scale is not representable in this model).
 *
 * @module floorplan/selection/handles
 * @category FloorPlan
 */

import { ROTATE_HANDLE_TOUCH_SIZE_PX } from '../constants';
import type { CameraState } from '../models/camera';
import type { Bounds, Vec2 } from '../models/geometry';
import type { FloorObject } from '../models/object';
import { effectiveSize, objectCenter, rotatePoint } from '../utils/bounds';
import { worldToScreen } from '../utils/camera';

export type ResizeHandleId = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w';

export type HandleId = ResizeHandleId | 'rotate';

/** Per-handle scale directions: -1 = handle on the min side, +1 = max side. */
export const HANDLE_DIRECTIONS: Record<
  ResizeHandleId,
  { x: -1 | 0 | 1; y: -1 | 0 | 1 }
> = {
  nw: { x: -1, y: -1 },
  n: { x: 0, y: -1 },
  ne: { x: 1, y: -1 },
  e: { x: 1, y: 0 },
  se: { x: 1, y: 1 },
  s: { x: 0, y: 1 },
  sw: { x: -1, y: 1 },
  w: { x: -1, y: 0 },
};

/** Minimum object size (world units) resize can shrink to. */
export const MIN_RESIZE_SIZE = 8;

/** World-space offset of the rotate handle above the frame's top edge. */
export const rotateHandleOffset = (zoom: number): number => 28 / zoom;

/** The 9 handle positions (world coords) for a selection frame. */
export const handleWorldPositions = (
  frame: Bounds,
  zoom: number,
): { id: HandleId; x: number; y: number }[] => {
  const cx = frame.x + frame.width / 2;
  const cy = frame.y + frame.height / 2;
  const x2 = frame.x + frame.width;
  const y2 = frame.y + frame.height;
  return [
    { id: 'nw', x: frame.x, y: frame.y },
    { id: 'n', x: cx, y: frame.y },
    { id: 'ne', x: x2, y: frame.y },
    { id: 'e', x: x2, y: cy },
    { id: 'se', x: x2, y: y2 },
    { id: 's', x: cx, y: y2 },
    { id: 'sw', x: frame.x, y: y2 },
    { id: 'w', x: frame.x, y: cy },
    { id: 'rotate', x: cx, y: frame.y - rotateHandleOffset(zoom) },
  ];
};

/**
 * Which handle (if any) a SCREEN-space touch lands on. Nearest handle
 * within the touch box wins, so overlapping targets at low zoom stay
 * predictable. The rotate handle gets its own, LARGER touch box
 * (`rotateTouchSizePx`, default `ROTATE_HANDLE_TOUCH_SIZE_PX`) — it floats
 * outside the frame where a fat target can't shadow anything, and the
 * shared 28px box made it near-impossible to grab on touch.
 */
export const hitTestHandle = (
  screen: Vec2,
  frame: Bounds,
  camera: CameraState,
  touchSizePx: number,
  rotateTouchSizePx?: number,
): HandleId | null => {
  const rotateSize =
    rotateTouchSizePx === undefined
      ? ROTATE_HANDLE_TOUCH_SIZE_PX
      : rotateTouchSizePx;
  let best: HandleId | null = null;
  let bestDistance = Number.POSITIVE_INFINITY;
  for (const handle of handleWorldPositions(frame, camera.zoom)) {
    const half = (handle.id === 'rotate' ? rotateSize : touchSizePx) / 2;
    const point = worldToScreen({ x: handle.x, y: handle.y }, camera);
    const dx = Math.abs(screen.x - point.x);
    const dy = Math.abs(screen.y - point.y);
    if (dx > half || dy > half) continue;
    const distance = dx * dx + dy * dy;
    if (distance < bestDistance) {
      bestDistance = distance;
      best = handle.id;
    }
  }
  return best;
};

/** The fixed point a resize scales around (opposite the dragged handle). */
export const resizeAnchor = (frame: Bounds, handle: ResizeHandleId): Vec2 => {
  const dir = HANDLE_DIRECTIONS[handle];
  return {
    x:
      dir.x === 0
        ? frame.x + frame.width / 2
        : dir.x === 1
          ? frame.x
          : frame.x + frame.width,
    y:
      dir.y === 0
        ? frame.y + frame.height / 2
        : dir.y === 1
          ? frame.y
          : frame.y + frame.height,
  };
};

/**
 * Scale factor for one axis given the drag delta along it. Worklet — runs
 * per gesture frame on the UI thread. `minSize` defaults INSIDE the body:
 * the Reanimated plugin does not capture module constants referenced only
 * in a default parameter value, so `= MIN_RESIZE_SIZE` in the signature
 * threw "Property 'MIN_RESIZE_SIZE' doesn't exist" on the native UI thread
 * (web never serializes worklets, which is why it only surfaced on device).
 */
export const scaleFromDelta = (
  size: number,
  direction: number,
  delta: number,
  minSize?: number,
): number => {
  'worklet';

  const effectiveMin = minSize === undefined ? MIN_RESIZE_SIZE : minSize;
  if (direction === 0 || size <= 0) return 1;
  let next = size + direction * delta;
  if (next < effectiveMin) next = effectiveMin;
  return next / size;
};

/** Commit-time patch for one object under an anchor-relative frame scale. */
export const applyFrameScale = (
  object: FloorObject,
  anchor: Vec2,
  sx: number,
  sy: number,
): { x: number; y: number; width: number; height: number } => ({
  x: anchor.x + (object.x - anchor.x) * sx,
  y: anchor.y + (object.y - anchor.y) * sy,
  width: object.width * sx,
  height: object.height * sy,
});

/**
 * Magnetic rotation: snap to 15° multiples when within `magnetDeg`, and
 * round to whole degrees otherwise. Worklet — runs per gesture frame.
 */
export const magneticAngle = (
  deltaDeg: number,
  magnetic: boolean,
  magnetDeg: number = 4,
): number => {
  'worklet';

  if (!magnetic) return Math.round(deltaDeg * 10) / 10;
  const nearest = Math.round(deltaDeg / 15) * 15;
  if (Math.abs(deltaDeg - nearest) <= magnetDeg) return nearest;
  return Math.round(deltaDeg * 10) / 10;
};

/**
 * Commit-time patches for rotating objects by `deltaDeg` around a shared
 * pivot: each object's CENTER orbits the pivot; its own rotation adds the
 * delta.
 */
export const rotationPatches = (
  objects: readonly FloorObject[],
  pivot: Vec2,
  deltaDeg: number,
): { id: string; next: { x: number; y: number; rotation: number } }[] => {
  const patches: {
    id: string;
    next: { x: number; y: number; rotation: number };
  }[] = [];
  for (const object of objects) {
    const center = objectCenter(object);
    const nextCenter = rotatePoint(center, pivot, deltaDeg);
    const size = effectiveSize(object);
    patches.push({
      id: object.id,
      next: {
        x: nextCenter.x - size.width / 2,
        y: nextCenter.y - size.height / 2,
        rotation: (object.rotation + deltaDeg) % 360,
      },
    });
  }
  return patches;
};
